import {useEffect, useRef, useState} from "react"
import {Box, Chip, CircularProgress, Stack, Typography} from "@mui/joy"
import {Trophy} from "lucide-react"
import {useTranslation} from "react-i18next"
import {ClickAwayListener} from "@mui/base"
import Card from "./Card"
import Input from "./Input"
import {useApiService} from "../hooks/useApiService"
import {searchLeagues} from "../services/competitions"
import {LeagueResponse} from "../types/league"

export type CompetitionSearchFieldProps = {
    placeholder?: string
    selected?: LeagueResponse
    onSelect: (league: LeagueResponse) => void
}

const SEARCH_DEBOUNCE_MS = 500
/** The provider ignores anything shorter, so it is not worth a request */
const MIN_SEARCH_LENGTH = 3

/** A crest that fails to load leaves a hole, so the trophy takes over */
export const CompetitionCrest = ({league, size = 24}: { league: LeagueResponse, size?: number }) => {
    const [broken, setBroken] = useState(false)
    const logo = league.league?.logo

    if (!logo || broken)
        return <Trophy size={size - 2}/>

    return <Box component={"img"}
                src={logo}
                alt={league.league?.name}
                onError={() => setBroken(true)}
                sx={{width: size, height: size, objectFit: "contain", flexShrink: 0}}/>
}

/**
 * Competition search.
 *
 * Built like the team search, against the football data provider: competitions, their crests and
 * their calendars all come from there, and only the analysis of a match comes from elsewhere.
 */
const CompetitionSearchField = ({placeholder, selected, onSelect}: CompetitionSearchFieldProps) => {
    const apiService = useApiService()
    const {t} = useTranslation()

    const [query, setQuery] = useState(selected?.league?.name || "")
    const [results, setResults] = useState<LeagueResponse[]>([])
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)
    const [error, setError] = useState("")
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
    // Bumped per search so a slow answer cannot overwrite the results of a later one
    const runRef = useRef(0)
    const pickedName = useRef(selected?.league?.name)

    useEffect(() => {
        const value = query.trim()

        if (value.length < MIN_SEARCH_LENGTH || value === pickedName.current) {
            setResults([])
            setLoading(false)
            return
        }

        setLoading(true)
        setError("")

        debounceRef.current = setTimeout(async () => {
            const run = ++runRef.current

            try {
                const leagues = await searchLeagues(() => apiService.client(true, false), value)

                if (run !== runRef.current) return
                setResults(leagues)
            } catch (requestError) {
                if (run === runRef.current) {
                    setResults([])
                    setError(requestError?.["message"] || t("competitionSearchUnavailable"))
                }
            } finally {
                if (run === runRef.current) setLoading(false)
            }
        }, SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(debounceRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query])

    const select = (league: LeagueResponse) => {
        clearTimeout(debounceRef.current)
        runRef.current += 1
        pickedName.current = league.league?.name

        setQuery(league.league?.name || "")
        setResults([])
        setLoading(false)
        setOpen(false)
        onSelect(league)
    }

    const searched = query.trim().length >= MIN_SEARCH_LENGTH && query.trim() !== pickedName.current

    return <Stack gap={0.5}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Stack gap={0.5}>
                <Input placeholder={placeholder}
                       value={query}
                       onChange={event => {
                           setQuery(event.target.value)
                           setOpen(true)
                       }}
                       onFocus={() => setOpen(true)}
                       endDecorator={loading && <CircularProgress size={"sm"}/>}/>

                {open && !!results.length && <Card variant={"outlined"}
                                                   sx={{
                                                       maxHeight: "20rem",
                                                       overflowY: "auto",
                                                       backgroundColor: "background.surface",
                                                       padding: 0,
                                                       boxShadow: "lg",
                                                       zIndex: 20
                                                   }}>
                    <Stack>
                        {results.map(league => <Stack key={league.league?.id}
                                                      direction={"row"}
                                                      alignItems={"center"}
                                                      justifyContent={"space-between"}
                                                      gap={1}
                                                      paddingX={{xs: 2, sm: 3}}
                                                      paddingY={1.25}
                                                      onClick={() => select(league)}
                                                      sx={{cursor: "pointer", ":hover": {backgroundColor: "primary.100"}}}>
                            <Stack direction={"row"} alignItems={"center"} gap={1.5} minWidth={0}>
                                <CompetitionCrest league={league}/>
                                <Typography level={"body-sm"} sx={{overflowWrap: "anywhere"}}>
                                    {league.league?.name}
                                </Typography>
                            </Stack>
                            {league.country?.name && <Chip size={"sm"} variant={"soft"} sx={{flexShrink: 0}}>
                                {league.country.name}
                            </Chip>}
                        </Stack>)}
                    </Stack>
                </Card>}
            </Stack>
        </ClickAwayListener>

        {error
            ? <Typography level={"body-xs"} color={"danger"}>{error}</Typography>
            : searched && !loading && !results.length &&
            <Typography level={"body-xs"} textColor={"text.tertiary"}>{t("competitionSearchNoResult")}</Typography>}
    </Stack>
}

export default CompetitionSearchField
